import type { ReactNode } from "react";

import { formatMoney, formatMoneyExact } from "../derive/reports.js";

/* ─────────────────────────────────────────────────────────── containers ── */

export function Card({
  title,
  action,
  children,
  flush = false,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  /** rows run edge to edge — the card supplies no horizontal padding */
  flush?: boolean;
}) {
  return (
    <section
      className="overflow-hidden rounded-2xl border border-line bg-surface"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      {(title || action) && (
        <header className="flex items-baseline justify-between gap-3 px-4 pt-4 pb-3">
          {title && <h2 className="text-[13px] font-semibold tracking-wide text-ink-2">{title}</h2>}
          {action}
        </header>
      )}
      <div className={flush ? "" : "px-4 pb-4" + (title || action ? "" : " pt-4")}>{children}</div>
    </section>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2.5 flex items-baseline justify-between gap-3 px-1">
      <h2 className="text-[11px] font-bold uppercase tracking-[0.09em] text-ink-3">{children}</h2>
      {action}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-10 text-center text-sm text-ink-3">{children}</p>;
}

/* ──────────────────────────────────────────────────────────────── money ── */

/**
 * Money never wears a series colour — figures stay in text ink, and meaning is
 * carried by an adjacent mark or an explicit Dr/Cr. `tone` exists only for
 * deltas, where direction genuinely is the message.
 */
export function Money({
  amount,
  exact = false,
  signed = false,
  tone = false,
}: {
  amount: number;
  exact?: boolean;
  signed?: boolean;
  tone?: boolean;
}) {
  const text = exact ? formatMoneyExact(Math.abs(amount)) : formatMoney(Math.abs(amount));
  const colour = !tone ? "" : amount < 0 ? "text-bad" : amount > 0 ? "text-good" : "";
  return (
    <span className={`tabular ${colour}`}>
      {signed && amount !== 0 ? (amount < 0 ? "−" : "+") : ""}
      {text}
    </span>
  );
}

/** Dr / Cr marker. Small, muted, and always beside the figure it qualifies. */
export function DrCr({ amount }: { amount: number }) {
  if (amount === 0) return null;
  return (
    <span className="ml-1 text-[10px] font-semibold uppercase text-ink-3">
      {amount < 0 ? "Dr" : "Cr"}
    </span>
  );
}

/* ───────────────────────────────────────────────────────────────── tiles ── */

export function Stat({
  label,
  value,
  sub,
  accent = false,
  onClick,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  accent?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <div className="text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-3">{label}</div>
      <div className="mt-1 text-[22px] font-semibold leading-tight tracking-tight text-ink">
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-ink-3">{sub}</div>}
    </>
  );

  const shell =
    "rounded-2xl border p-4 text-left " +
    (accent ? "border-transparent bg-accent-wash" : "border-line bg-surface");

  if (!onClick) return <div className={shell} style={{ boxShadow: "var(--shadow-card)" }}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${shell} transition-transform active:scale-[0.98]`}
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      {body}
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────── rows ── */

export function Rows({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-line">{children}</ul>;
}

/** Initials disc. Gives a long list of party names a scannable left edge. */
export function Avatar({ name }: { name: string }) {
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";

  return (
    <span
      aria-hidden
      className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-2 text-[11px] font-bold text-ink-2"
    >
      {initials}
    </span>
  );
}

export function Row({
  title,
  sub,
  right,
  rightSub,
  avatar,
  onClick,
}: {
  title: string;
  sub?: string;
  right?: ReactNode;
  rightSub?: ReactNode;
  avatar?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      {avatar && <Avatar name={title} />}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] leading-snug text-ink">{title}</span>
        {sub && <span className="mt-0.5 block truncate text-xs text-ink-3">{sub}</span>}
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-[15px] leading-snug font-medium text-ink">{right}</span>
        {rightSub && <span className="mt-0.5 block text-xs text-ink-3">{rightSub}</span>}
      </span>
      {onClick && (
        <svg viewBox="0 0 24 24" className="-mr-1 h-4 w-4 shrink-0 text-ink-3" aria-hidden>
          <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </>
  );

  if (!onClick) return <li className="flex items-center gap-3 px-4 py-3">{content}</li>;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-surface-2"
      >
        {content}
      </button>
    </li>
  );
}

/* ───────────────────────────────────────────────────────────── controls ── */

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex gap-1 rounded-xl bg-surface-2 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={
            "flex-1 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors " +
            (o.value === value ? "bg-surface text-ink shadow-sm" : "text-ink-3")
          }
          style={o.value === value ? { boxShadow: "var(--shadow-card)" } : undefined}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A switch with its downside printed underneath.
 *
 * The risk line is not fine print and is not hidden behind an info icon: these
 * switches turn on behaviour that can leak figures out of the encrypted copy or
 * lock someone out of their own books, and a user cannot consent to a risk they
 * have to go looking for.
 */
export function Toggle({
  title,
  blurb,
  risk,
  checked,
  onChange,
}: {
  title: string;
  blurb: string;
  risk?: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <li className="px-4 py-3.5">
      <label className="flex cursor-pointer items-start gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] leading-snug text-ink">{title}</span>
          <span className="mt-1 block text-xs leading-relaxed text-ink-3">{blurb}</span>
          {risk && (
            <span className="mt-1.5 block text-xs leading-relaxed text-warn">{risk}</span>
          )}
        </span>

        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span
          aria-hidden
          className={
            "relative mt-0.5 h-6 w-10 shrink-0 rounded-full transition-colors " +
            (checked ? "bg-accent" : "bg-surface-2 border border-line")
          }
        >
          <span
            className={
              "absolute top-1 h-4 w-4 rounded-full bg-surface shadow transition-all " +
              (checked ? "left-5" : "left-1")
            }
            style={{ boxShadow: "var(--shadow-card)" }}
          />
        </span>
      </label>
    </li>
  );
}

/** Wide content scrolls inside itself — the page must never scroll sideways. */
export function Scroller({ children }: { children: ReactNode }) {
  return <div className="no-bar -mx-4 overflow-x-auto px-4">{children}</div>;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-3">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <span className="mt-1.5 block text-xs text-ink-3">{hint}</span>}
    </label>
  );
}

export const inputClass =
  "w-full rounded-xl border border-line bg-surface-2 px-3.5 py-3 text-[15px] text-ink " +
  "placeholder:text-ink-3 focus:border-accent focus:bg-surface focus:outline-none";

export const buttonClass =
  "w-full rounded-xl bg-accent px-4 py-3 text-[15px] font-semibold text-accent-ink " +
  "transition-transform active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50";

export const ghostButtonClass =
  "rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-2 transition-colors active:bg-surface-2";
